import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Trophy, Sparkles } from "lucide-react";
import { toast } from "sonner";

const GoalCompletionModal = ({ isOpen, onClose, revenueGoal }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const fullText = "Interested in using the real thing? Enter your name and business email to get started.";

  useEffect(() => {
    if (!isOpen) {
      setDisplayedText("");
      setShowForm(false);
      return;
    }

    let index = 0;
    const typingSpeed = 30; // milliseconds per character

    const timer = setInterval(() => {
      if (index <= fullText.length) {
        setDisplayedText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(timer);
        setShowForm(true);
      }
    }, typingSpeed);

    return () => clearInterval(timer);
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);

    try {
      // Import API from App.js
      const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      // Submit to backend
      const response = await fetch(`${API}/api/goal-completion-lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          revenueGoal: revenueGoal
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log("Goal completion lead submitted:", data);
        toast.success("Thanks! We'll be in touch soon!");
        setFormData({ name: "", email: "" });
        onClose();
      } else {
        throw new Error(data.message || "Failed to submit");
      }
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
      <Card className="relative w-full max-w-xl bg-gradient-to-br from-[#0f1419] to-[#1a1f24] border-[#3dd9c5]/50 p-8 shadow-2xl animate-in zoom-in-95 duration-300">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Celebration icon */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <Trophy className="h-16 w-16 text-[#3dd9c5] animate-bounce" />
            <Sparkles className="absolute -top-2 -right-2 h-6 w-6 text-yellow-400 animate-pulse" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-3xl font-bold text-white text-center mb-2">
          🎉 Goal Achieved!
        </h2>
        <p className="text-center text-gray-400 mb-6">
          You've reached ${(revenueGoal / 1000000).toFixed(1)}M in revenue!
        </p>

        {/* Typewriter text */}
        <div className="mb-8 min-h-[60px]">
          <p className="text-lg text-white text-center">
            {displayedText}
            {displayedText.length < fullText.length && (
              <span className="inline-block w-0.5 h-5 bg-[#3dd9c5] ml-1 animate-pulse"></span>
            )}
          </p>
        </div>

        {/* Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div>
              <Label htmlFor="name" className="text-white mb-2 block">
                Name
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={handleChange}
                className="bg-white/5 border-[#3dd9c5]/50 text-white placeholder:text-gray-500 focus:border-[#3dd9c5]"
                required
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-white mb-2 block">
                Business Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="john@company.com"
                value={formData.email}
                onChange={handleChange}
                className="bg-white/5 border-[#3dd9c5]/50 text-white placeholder:text-gray-500 focus:border-[#3dd9c5]"
                required
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                className="flex-1 bg-transparent border-gray-700 text-white hover:bg-white/10"
                disabled={isSubmitting}
              >
                Maybe Later
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#3dd9c5] hover:bg-[#35c4b5] text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Get Started"}
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default GoalCompletionModal;
